/**
 * SAM-466 — late-cancellation penalty percent (`settings.cancellation_policy.penalty_percent`).
 *
 * Mirrors sam-lambda-functions functions/booking/cancellation_policy.py::credit_percent: the credit a
 * guest gets when cancelling a paid booking is paid (net of refunds) x credit percent, where the
 * credit percent is
 *   - 100 when cancelled in time, or when the policy has no penalty fee;
 *   - 100 - penalty_percent (clamped to 0-100) when cancelled late with penalty_fee on;
 *   - 100 when penalty_fee is on but no penalty_percent is set (a missing field must not cost the
 *     guest their money — Rodrigo, 2026-10-01).
 */

/** Form value for the field: '' when the stored policy has no usable percent. */
export function penaltyPercentFromSettings(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '' || typeof raw === 'boolean') return '';
  const n = Number(raw);
  return Number.isFinite(n) ? String(n) : '';
}

/** Validation message for the input, or null when valid. '' (not set) is valid. */
export function penaltyPercentError(value: string): string | null {
  const text = value.trim();
  if (text === '') return null;
  const n = Number(text);
  if (!Number.isInteger(n) || n < 0 || n > 100) return 'Enter a whole number from 0 to 100.';
  return null;
}

/** The value to store, or undefined when the field is empty or invalid (key omitted). */
export function penaltyPercentForSave(value: string): number | undefined {
  if (value.trim() === '' || penaltyPercentError(value)) return undefined;
  return Number(value);
}

/** Credit percent (0-100) for a LATE cancellation under this policy — same rule as the backend. */
export function lateCreditPercent(penaltyFee: boolean, penaltyPercent: string): number {
  if (!penaltyFee) return 100;
  const text = penaltyPercent.trim();
  if (text === '') return 100;
  const n = Number(text);
  if (!Number.isFinite(n)) return 100;
  return 100 - Math.min(100, Math.max(0, n));
}

function hoursLabel(hoursText: string): string {
  const n = Number(hoursText);
  const hours = Number.isFinite(n) && n > 0 ? n : 24; // the save falls back to 24h too
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}

/** One-line live example, e.g. «Cancelling later than 4 hours before: guest gets back 50% (credit or refund)». */
export function penaltyExampleText(timeLimitHours: string, penaltyFee: boolean, penaltyPercent: string): string {
  return `Cancelling later than ${hoursLabel(timeLimitHours)} before: guest gets back ${lateCreditPercent(penaltyFee, penaltyPercent)}% (credit or refund)`;
}
